<template>
  <div class="workspace-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <div class="workspace-container">
      <div class="workspace-grid">
        <!-- LEFT COLUMN -->
        <div class="left-column">
          <!-- Product Overview Card -->
          <div class="overview-card">
            <div class="overview-header">
              <div class="header-main">
                <div class="product-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="currentColor" stroke-width="2"/>
                    <path d="M16 21V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V21" stroke="currentColor" stroke-width="2"/>
                  </svg>
                </div>
                <div class="product-info">
                  <h1 class="product-name">{{ product ? product.name : 'Loading...' }}</h1>
                  <div class="product-meta">
                    <div class="presence-avatars">
                      <div
                        v-for="v in viewers.slice(0, 5)"
                        :key="v.uid"
                        class="presence-avatar"
                        :style="{ background: avatarGradient(v.email || v.uid) }"
                        :title="v.email"
                      >
                        {{ initials(v.email || v.uid) }}
                      </div>
                      <div v-if="viewers.length > 0" class="presence-count">
                        {{ viewers.length }} online
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <span :class="['product-status', 'status-' + (product?.status || 'active').toLowerCase()]">
                {{ product?.status || 'Active' }}
              </span>
            </div>

            <div class="overview-body">
              <div class="section-label">Summary</div>
              <p class="product-summary">{{ product?.summary || '' }}</p>

              <!-- KPI Cards -->
              <div class="kpi-grid">
                <div class="kpi-mini kpi-tasks">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M9.33333 16L13.3333 20L22.6667 10.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M28 16C28 22.6274 22.6274 28 16 28C9.37258 28 4 22.6274 4 16C4 9.37258 9.37258 4 16 4C22.6274 4 28 9.37258 28 16Z" stroke="currentColor" stroke-width="2"/>
                  </svg>
                  <div>
                    <div class="kpi-value">{{ openTaskCount }}</div>
                    <div class="kpi-label">Open Tasks</div>
                  </div>
                </div>

                <div class="kpi-mini kpi-contacts">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M21.3333 28V25.3333C21.3333 23.9188 20.7714 22.5623 19.7712 21.5621C18.771 20.5619 17.4145 20 16 20H6.66667C5.25218 20 3.89562 20.5619 2.89543 21.5621C1.89524 22.5623 1.33333 23.9188 1.33333 25.3333V28" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M11.3333 14.6667C14.2789 14.6667 16.6667 12.2789 16.6667 9.33333C16.6667 6.38781 14.2789 4 11.3333 4C8.38781 4 6 6.38781 6 9.33333C6 12.2789 8.38781 14.6667 11.3333 14.6667Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M28 28.0001V25.3334C27.9987 24.1619 27.6011 23.0252 26.8732 22.1084C26.1452 21.1915 25.1291 20.5489 24 20.2801" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M20 4.28003C21.1314 4.54803 22.1497 5.19104 22.8789 6.10951C23.6082 7.02798 24.0063 8.16689 24.0063 9.34003C24.0063 10.5132 23.6082 11.6521 22.8789 12.5705C22.1497 13.489 21.1314 14.132 20 14.4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <div>
                    <div class="kpi-value">{{ contacts.length }}</div>
                    <div class="kpi-label">Stakeholders</div>
                  </div>
                </div>

                <div class="kpi-mini kpi-files">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M18.6667 2.66666H8C6.58551 2.66666 5.22896 3.22856 4.22876 4.22876C3.22857 5.22895 2.66667 6.58551 2.66667 8V24C2.66667 25.4145 3.22857 26.771 4.22876 27.7712C5.22896 28.7714 6.58551 29.3333 8 29.3333H24C25.4145 29.3333 26.771 28.7714 27.7712 27.7712C28.7714 26.771 29.3333 25.4145 29.3333 24V13.3333L18.6667 2.66666Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M18.6667 2.66666V13.3333H29.3333" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <div>
                    <div class="kpi-value">{{ files.length }}</div>
                    <div class="kpi-label">Files</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Links Card -->
          <div class="links-card">
            <div class="card-header">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M8.33333 10.8333C8.69103 11.3118 9.14777 11.7074 9.67235 11.9943C10.1969 12.2812 10.7764 12.4529 11.3721 12.4984C11.9679 12.5438 12.5671 12.462 13.1307 12.258C13.6943 12.054 14.2102 11.7325 14.6458 11.3142L17.3125 8.64749C18.0846 7.83865 18.5104 6.76495 18.5005 5.65112C18.4906 4.53729 18.0457 3.47138 17.2598 2.67553C16.474 1.87968 15.4081 1.42477 14.2942 1.40484C13.1804 1.38492 12.1067 1.80076 11.2979 2.57291L9.79167 4.07291" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.6667 9.16668C11.309 8.68821 10.8522 8.29257 10.3277 8.00569C9.80309 7.71881 9.22359 7.54706 8.62784 7.50163C8.03209 7.4562 7.43291 7.53801 6.86931 7.742C6.30571 7.94599 5.78979 8.26754 5.35417 8.68585L2.6875 11.3525C1.91535 12.1614 1.48951 13.2351 1.49944 14.3489C1.50936 15.4627 1.95428 16.5286 2.74012 17.3245C3.52597 18.1203 4.59188 18.5653 5.70571 18.5752C6.81954 18.5851 7.89324 18.1593 8.70208 17.3871L10.2021 15.8871" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Quick Links</span>
              <button class="btn-add-link" @click="createLinkDialog = true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3.33334V12.6667M3.33333 8H12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Add
              </button>
            </div>
            <div class="links-body">
              <a
                v-for="l in links"
                :key="l.id"
                :href="l.url"
                target="_blank"
                class="link-chip"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path v-if="l.type === 'sheet'" d="M13.3333 2H2.66667C1.92667 2 1.33333 2.59333 1.33333 3.33333V12.6667C1.33333 13.4067 1.92667 14 2.66667 14H13.3333C14.0733 14 14.6667 13.4067 14.6667 12.6667V3.33333C14.6667 2.59333 14.0733 2 13.3333 2ZM5.33333 11.3333H3.33333V9.33333H5.33333V11.3333ZM5.33333 8H3.33333V6H5.33333V8ZM9.33333 11.3333H7.33333V9.33333H9.33333V11.3333ZM9.33333 8H7.33333V6H9.33333V8ZM13.3333 11.3333H11.3333V9.33333H13.3333V11.3333ZM13.3333 8H11.3333V6H13.3333V8Z" fill="currentColor"/>
                  <path v-else-if="l.type === 'deck'" d="M13.3333 2H2.66667C1.92667 2 1.33333 2.59333 1.33333 3.33333V12.6667C1.33333 13.4067 1.92667 14 2.66667 14H13.3333C14.0733 14 14.6667 13.4067 14.6667 12.6667V3.33333C14.6667 2.59333 14.0733 2 13.3333 2ZM8 10L4.66667 6.66667H11.3333L8 10Z" fill="currentColor"/>
                  <path v-else-if="l.type === 'calendar'" d="M12.6667 2.66666H11.3333V1.33333H10V2.66666H6V1.33333H4.66667V2.66666H3.33333C2.6 2.66666 2 3.26666 2 4V13.3333C2 14.0667 2.6 14.6667 3.33333 14.6667H12.6667C13.4 14.6667 14 14.0667 14 13.3333V4C14 3.26666 13.4 2.66666 12.6667 2.66666ZM12.6667 13.3333H3.33333V6.66666H12.6667V13.3333Z" fill="currentColor"/>
                  <path v-else d="M8.33333 10.8333C8.69103 11.3118 9.14777 11.7074 9.67235 11.9943C10.1969 12.2812 10.7764 12.4529 11.3721 12.4984C11.9679 12.5438 12.5671 12.462 13.1307 12.258C13.6943 12.054 14.2102 11.7325 14.6458 11.3142L17.3125 8.64749C18.0846 7.83865 18.5104 6.76495 18.5005 5.65112C18.4906 4.53729 18.0457 3.47138 17.2598 2.67553C16.474 1.87968 15.4081 1.42477 14.2942 1.40484C13.1804 1.38492 12.1067 1.80076 11.2979 2.57291L9.79167 4.07291" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ l.label }}
              </a>
              <div v-if="!links.length" class="empty-links">No links yet</div>
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="right-column">
          <div class="workspace-card">
            <!-- Tabs Header -->
            <div class="tabs-header">
              <button
                v-for="(tabItem, idx) in tabs"
                :key="idx"
                :class="['tab-button', { active: tab === idx }]"
                @click="tab = idx"
              >
                <component :is="'svg'" width="18" height="18" viewBox="0 0 18 18" fill="none" v-html="tabItem.icon"></component>
                <span>{{ tabItem.label }}</span>
              </button>
            </div>

            <!-- Tab Content -->
            <div class="tab-content">
              <!-- NOTES TAB -->
              <div v-if="tab === 0" class="tab-panel">
                <div class="notes-form">
                  <textarea
                    v-model="noteText"
                    placeholder="Add a quick note..."
                    rows="3"
                  ></textarea>
                  <div class="form-actions">
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="isPrivate" />
                      <span>Private to me</span>
                    </label>
                    <button
                      class="btn-primary"
                      :disabled="!noteText.trim() || saving"
                      @click="addNote"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M14.6667 1.33334L7.33333 8.66668M14.6667 1.33334L10 14.6667L7.33333 8.66668M14.6667 1.33334L1.33333 6.00001L7.33333 8.66668" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      {{ saving ? 'Saving...' : 'Save Note' }}
                    </button>
                  </div>
                </div>

                <div class="section-divider"></div>

                <div class="notes-header">
                  <span class="section-title">Recent Notes</span>
                  <span class="note-count">{{ visibleNotes.length }} shown</span>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="showPrivate" />
                    <span>Show private</span>
                  </label>
                </div>

                <div v-if="loadingNotes" class="loading-state">
                  <div class="loader"></div>
                  <p>Loading notes...</p>
                </div>

                <div v-else-if="visibleNotes.length > 0" class="notes-list">
                  <div v-for="n in visibleNotes" :key="n.id" class="note-item">
                    <div class="note-avatar" :style="{ background: avatarGradient(n.authorName) }">
                      {{ initials(n.authorName) }}
                    </div>
                    <div class="note-content">
                      <div class="note-meta">
                        <span class="note-author">{{ n.authorName }}</span>
                        <span v-if="n.isPrivate" class="note-badge">private</span>
                        <span class="note-time">{{ prettyTs(n.createdAt) }}</span>
                      </div>
                      <div class="note-text">{{ n.text }}</div>
                    </div>
                    <button
                      v-if="n.authorUid === meUid"
                      class="btn-delete"
                      @click="delNote(n)"
                      :disabled="delId === n.id"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2 6 1.33333 6.66667 1.33333H9.33333C10 1.33333 10.6667 2 10.6667 2.66667V4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <div v-else class="empty-state">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M34 6H14C12.8954 6 12 6.89543 12 8V40C12 41.1046 12.8954 42 14 42H34C35.1046 42 36 41.1046 36 40V8C36 6.89543 35.1046 6 34 6Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M18 16H30M18 24H30M18 32H24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <p>No notes yet</p>
                </div>
              </div>

              <!-- TASKS TAB -->
              <div v-if="tab === 1" class="tab-panel">
                <div class="task-form">
                  <input
                    v-model="taskForm.title"
                    type="text"
                    placeholder="Task title..."
                    class="task-input"
                  />
                  <div class="task-form-row">
                    <select v-model="taskForm.priority" class="task-select">
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                    </select>
                    <input
                      v-model="taskForm.dueDate"
                      type="date"
                      class="task-date"
                    />
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="taskForm.private" />
                      <span>Private</span>
                    </label>
                    <button
                      class="btn-primary"
                      :disabled="!taskForm.title.trim() || savingTask"
                      @click="addTask"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3.33334V12.6667M3.33333 8H12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      Add
                    </button>
                  </div>
                </div>

                <div class="task-filters">
                  <div class="filter-chips">
                    <button
                      v-for="f in ['all', 'mine', 'open', 'done']"
                      :key="f"
                      :class="['filter-chip', { active: taskFilter === f }]"
                      @click="taskFilter = f"
                    >
                      {{ f }}
                    </button>
                  </div>
                  <select v-model="taskSort" class="sort-select">
                    <option value="due-asc">Due (soonest)</option>
                    <option value="due-desc">Due (latest)</option>
                    <option value="prio">Priority</option>
                  </select>
                </div>

                <div v-if="loadingTasks" class="loading-state">
                  <div class="loader"></div>
                  <p>Loading tasks...</p>
                </div>

                <div v-else-if="filteredTasks.length > 0" class="tasks-list">
                  <div v-for="t in filteredTasks" :key="t.id" class="task-item">
                    <label class="task-checkbox">
                      <input
                        type="checkbox"
                        :checked="t.status === 'done'"
                        @change="toggleTask(t)"
                      />
                      <span class="checkmark"></span>
                    </label>
                    <div class="task-content">
                      <div :class="['task-title', { completed: t.status === 'done' }]">
                        {{ t.title }}
                      </div>
                      <div class="task-meta">
                        <span :class="['priority-badge', 'priority-' + (t.priority || 'normal')]">
                          {{ t.priority || 'normal' }}
                        </span>
                        <span v-if="t.dueAt" :class="['due-date', { overdue: isOverdue(t) }]">
                          Due {{ prettyDate(t.dueAt) }}
                        </span>
                        <span v-else class="no-due">No due date</span>
                      </div>
                    </div>
                    <button
                      v-if="t.authorUid === meUid"
                      class="btn-delete"
                      @click="deleteTask(t)"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <div v-else class="empty-state">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M40 12L18 34L8 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <p>No tasks match your filters</p>
                </div>
              </div>

              <!-- CONTACTS TAB -->
              <div v-if="tab === 2" class="tab-panel">
                <div class="contacts-header">
                  <input
                    v-model="contactSearch"
                    type="text"
                    placeholder="Search contacts..."
                    class="search-input"
                  />
                  <button class="btn-primary" @click="contactDialog = true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 3.33334V12.6667M3.33333 8H12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    New Contact
                  </button>
                </div>

                <div v-if="loadingContacts" class="loading-state">
                  <div class="loader"></div>
                  <p>Loading contacts...</p>
                </div>

                <div v-else-if="filteredContacts.length > 0" class="contacts-list">
                  <div v-for="c in filteredContacts" :key="c.id" class="contact-item">
                    <div class="contact-avatar" :style="{ background: avatarGradient(c.name || c.email) }">
                      {{ initials(c.name || c.email) }}
                    </div>
                    <div class="contact-content">
                      <div class="contact-name">{{ c.name }}</div>
                      <div class="contact-meta">
                        <span>{{ c.title }}</span>
                        <a :href="'mailto:' + c.email">{{ c.email }}</a>
                        <span v-if="c.phone">{{ c.phone }}</span>
                      </div>
                    </div>
                    <span v-if="c.role" class="contact-role">{{ c.role }}</span>
                    <button class="btn-delete" @click="removeContact(c)">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <div v-else class="empty-state">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M32 42V38C32 35.8783 31.1571 33.8434 29.6569 32.3431C28.1566 30.8429 26.1217 30 24 30H10C7.87827 30 5.84344 30.8429 4.34315 32.3431C2.84285 33.8434 2 35.8783 2 38V42" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M17 22C21.4183 22 25 18.4183 25 14C25 9.58172 21.4183 6 17 6C12.5817 6 9 9.58172 9 14C9 18.4183 12.5817 22 17 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <p>No contacts found</p>
                </div>
              </div>

              <!-- FILES TAB -->
              <div v-if="tab === 3" class="tab-panel">
                <div class="files-upload">
                  <div class="upload-area" @click="$refs.fileInput.click()" @drop.prevent="handleDrop" @dragover.prevent>
                    <input
                      ref="fileInput"
                      type="file"
                      multiple
                      @change="handleFileSelect"
                      style="display: none"
                    />
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <path d="M42 30V38C42 39.0609 41.5786 40.0783 40.8284 40.8284C40.0783 41.5786 39.0609 42 38 42H10C8.93913 42 7.92172 41.5786 7.17157 40.8284C6.42143 40.0783 6 39.0609 6 38V30M34 16L24 6M24 6L14 16M24 6V30" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <p class="upload-text">Click or drag files here to upload</p>
                    <p class="upload-hint">Supports images, documents, videos, and more</p>
                  </div>
                  <div v-if="fileToUpload.length > 0" class="files-preview">
                    <div v-for="(file, idx) in fileToUpload" :key="idx" class="file-preview">
                      <img v-if="file.type.startsWith('image/')" :src="getPreviewUrl(file)" alt="Preview" />
                      <svg v-else width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <path d="M18.6667 2.66666H8C6.58551 2.66666 5.22896 3.22856 4.22876 4.22876C3.22857 5.22895 2.66667 6.58551 2.66667 8V24C2.66667 25.4145 3.22857 26.771 4.22876 27.7712C5.22896 28.7714 6.58551 29.3333 8 29.3333H24C25.4145 29.3333 26.771 28.7714 27.7712 27.7712C28.7714 26.771 29.3333 25.4145 29.3333 24V13.3333L18.6667 2.66666Z" stroke="currentColor" stroke-width="2"/>
                      </svg>
                      <span class="file-name">{{ file.name }}</span>
                      <span class="file-size">{{ formatFileSize(file.size) }}</span>
                      <button class="btn-remove" @click="removeFileFromUpload(idx)">✕</button>
                    </div>
                  </div>
                  <button
                    v-if="fileToUpload.length > 0"
                    class="btn-upload"
                    :disabled="uploading"
                    @click="uploadFiles"
                  >
                    {{ uploading ? 'Uploading...' : `Upload ${fileToUpload.length} file${fileToUpload.length > 1 ? 's' : ''}` }}
                  </button>
                </div>

                <div class="files-header">
                  <span class="section-title">Uploaded Files</span>
                  <input
                    v-model="fileSearch"
                    type="text"
                    placeholder="Search files..."
                    class="search-input-small"
                  />
                </div>

                <div v-if="loadingFiles" class="loading-state">
                  <div class="loader"></div>
                  <p>Loading files...</p>
                </div>

                <div v-else-if="filteredFiles.length > 0" class="files-list">
                  <div v-for="f in filteredFiles" :key="f.id" class="file-item">
                    <div class="file-preview-thumb">
                      <img v-if="f.mime.startsWith('image/')" :src="f.url" alt="File" />
                      <svg v-else width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <path :d="getFileIconPath(f.mime)" fill="currentColor"/>
                      </svg>
                    </div>
                    <div class="file-content">
                      <a :href="f.url" target="_blank" class="file-name">{{ f.name }}</a>
                      <div class="file-meta">
                        <span>{{ formatFileSize(f.size) }}</span>
                        <span>{{ prettyTs(f.uploadedAt) }}</span>
                      </div>
                    </div>
                    <button class="btn-delete" @click="deleteFile(f)">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <div v-else class="empty-state">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M28 4H12C10.3431 4 9 5.34315 9 7V41C9 42.6569 10.3431 44 12 44H36C37.6569 44 39 42.6569 39 41V19L28 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M28 4V19H39" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <p>No files uploaded yet</p>
                </div>
              </div>

              <!-- ACTIVITY TAB -->
              <div v-if="tab === 4" class="tab-panel">
                <div v-if="loadingActivity" class="loading-state">
                  <div class="loader"></div>
                  <p>Loading activity...</p>
                </div>

                <div v-else-if="activity.length > 0" class="activity-timeline">
                  <div v-for="a in activity" :key="a.id" class="activity-item">
                    <div :class="['activity-icon', 'icon-' + getActivityType(a.type)]">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" v-html="getActivityIcon(a.type)"></svg>
                    </div>
                    <div class="activity-content">
                      <div class="activity-actor">{{ a.actorName }}</div>
                      <div class="activity-time">{{ prettyTs(a.createdAt) }}</div>
                      <div class="activity-message">{{ a.message }}</div>
                    </div>
                  </div>
                </div>

                <div v-else class="empty-state">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4C12.9543 4 4 12.9543 4 24C4 35.0457 12.9543 44 24 44Z" stroke="currentColor" stroke-width="2"/>
                    <path d="M24 12V24L32 28" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <p>No activity yet</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Dialog -->
    <div v-if="contactDialog" class="modal-overlay" @click.self="contactDialog = false">
      <div class="modal-dialog">
        <div class="modal-header">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M20 8V14M17 11H23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>New Contact</span>
          <button class="btn-close" @click="contactDialog = false">✕</button>
        </div>
        <div class="modal-body">
          <input v-model="contactForm.name" type="text" placeholder="Full name *" />
          <input v-model="contactForm.title" type="text" placeholder="Title" />
          <input v-model="contactForm.email" type="email" placeholder="Email *" />
          <input v-model="contactForm.phone" type="tel" placeholder="Phone" />
          <select v-model="contactForm.role">
            <option value="">Select role</option>
            <option value="Decision Maker">Decision Maker</option>
            <option value="Influencer">Influencer</option>
            <option value="User">User</option>
            <option value="Procurement">Procurement</option>
            <option value="Other">Other</option>
          </select>
          <textarea v-model="contactForm.notes" placeholder="Notes" rows="3"></textarea>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="contactDialog = false">Cancel</button>
          <button
            class="btn-primary"
            :disabled="!contactForm.name || !contactForm.email || savingContact"
            @click="addContact"
          >
            {{ savingContact ? 'Saving...' : 'Save Contact' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Link Dialog -->
    <div v-if="createLinkDialog" class="modal-overlay" @click.self="createLinkDialog = false">
      <div class="modal-dialog modal-sm">
        <div class="modal-header">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M10 13C10.4295 13.5741 10.9774 14.0492 11.6066 14.3929C12.2357 14.7367 12.9315 14.9411 13.6467 14.9923C14.3618 15.0435 15.0796 14.9404 15.7513 14.6898C16.4231 14.4392 17.0331 14.0471 17.54 13.54L20.54 10.54C21.4508 9.59699 21.9548 8.33397 21.9434 7.02299C21.932 5.71201 21.4061 4.45794 20.4791 3.5309C19.5521 2.60386 18.298 2.07802 16.987 2.06663C15.676 2.05524 14.413 2.55921 13.47 3.47L11.75 5.18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M14 11C13.5705 10.4259 13.0226 9.95083 12.3934 9.60707C11.7642 9.26331 11.0684 9.05889 10.3533 9.00768C9.63816 8.95646 8.92037 9.05961 8.24861 9.3102C7.57685 9.56079 6.96684 9.95292 6.45996 10.46L3.45996 13.46C2.54917 14.403 2.04519 15.666 2.05659 16.977C2.06798 18.288 2.59382 19.5421 3.52086 20.4691C4.4479 21.3961 5.70197 21.922 7.01295 21.9334C8.32393 21.9448 9.58694 21.4408 10.53 20.53L12.24 18.82" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Add Link</span>
          <button class="btn-close" @click="createLinkDialog = false">✕</button>
        </div>
        <div class="modal-body">
          <input v-model="linkForm.label" type="text" placeholder="Label *" />
          <input v-model="linkForm.url" type="url" placeholder="URL *" />
          <select v-model="linkForm.type">
            <option value="doc">Document</option>
            <option value="sheet">Spreadsheet</option>
            <option value="deck">Presentation</option>
            <option value="calendar">Calendar</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="createLinkDialog = false">Cancel</button>
          <button
            class="btn-primary"
            :disabled="!linkForm.label || !linkForm.url || savingLink"
            @click="addLink"
          >
            {{ savingLink ? 'Saving...' : 'Save Link' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div v-if="snack.show" class="toast-notification">
      <span>{{ snack.msg }}</span>
      <button @click="snack.show = false">✕</button>
    </div>
  </div>
</template>

<script>
  import { db, storage, auth } from '@/firebase'
import {
  doc, getDoc, collection, addDoc, onSnapshot, query,
  serverTimestamp, deleteDoc, where, updateDoc
} from 'firebase/firestore'
import { ref as sRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'

export default {
  name: 'StaffProductDetail',
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      tab: 0,
      product: null,
      tabs: [
        { label: 'Notes', icon: '<path d="M13.5 3H4.5C3.67157 3 3 3.67157 3 4.5V13.5C3 14.3284 3.67157 15 4.5 15H13.5C14.3284 15 15 14.3284 15 13.5V4.5C15 3.67157 14.3284 3 13.5 3Z" stroke="currentColor" stroke-width="1.5"/><path d="M6 6H12M6 9H12M6 12H9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' },
        { label: 'Tasks', icon: '<path d="M13.5 3H4.5C3.67157 3 3 3.67157 3 4.5V13.5C3 14.3284 3.67157 15 4.5 15H13.5C14.3284 15 15 14.3284 15 13.5V4.5C15 3.67157 14.3284 3 13.5 3Z" stroke="currentColor" stroke-width="1.5"/><path d="M6 9L8 11L12 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' },
        { label: 'Contacts', icon: '<path d="M9 13.5C11.4853 13.5 13.5 11.4853 13.5 9C13.5 6.51472 11.4853 4.5 9 4.5C6.51472 4.5 4.5 6.51472 4.5 9C4.5 11.4853 6.51472 13.5 9 13.5Z" stroke="currentColor" stroke-width="1.5"/><path d="M3 15.75C3 14.5074 3.49386 13.3155 4.37868 12.4307C5.26351 11.5459 6.45544 11.0521 7.69804 11.0521H10.302C11.5446 11.0521 12.7365 11.5459 13.6213 12.4307C14.5061 13.3155 15 14.5074 15 15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' },
        { label: 'Files', icon: '<path d="M10.5 3H4.5C3.67157 3 3 3.67157 3 4.5V13.5C3 14.3284 3.67157 15 4.5 15H13.5C14.3284 15 15 14.3284 15 13.5V7.5L10.5 3Z" stroke="currentColor" stroke-width="1.5"/><path d="M10.5 3V7.5H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' },
        { label: 'Activity', icon: '<path d="M9 13.5C11.4853 13.5 13.5 11.4853 13.5 9C13.5 6.51472 11.4853 4.5 9 4.5C6.51472 4.5 4.5 6.51472 4.5 9C4.5 11.4853 6.51472 13.5 9 13.5Z" stroke="currentColor" stroke-width="1.5"/><path d="M9 6V9L11.25 10.125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' }
      ],

      // Presence
      viewers: [],
      presenceInterval: null,

      // NOTES
      publicNotes: [],
      myPrivateNotes: [],
      unsubPublic: null,
      unsubPrivate: null,
      loadingNotes: true,
      noteText: '',
      isPrivate: false,
      showPrivate: true,
      saving: false,
      delId: '',
      meUid: '',
      meName: '',

      // TASKS
      tasks: [],
      unsubTasks: null,
      loadingTasks: true,
      taskForm: { title: '', priority: 'normal', dueDate: null, private: false },
      savingTask: false,
      taskFilter: 'all',
      taskSort: 'due-asc',

      // CONTACTS
      contacts: [],
      loadingContacts: true,
      unsubContacts: null,
      contactDialog: false,
      savingContact: false,
      contactSearch: '',
      contactForm: { name: '', title: '', email: '', phone: '', role: '', notes: '' },

      // FILES
      files: [],
      loadingFiles: true,
      unsubFiles: null,
      fileToUpload: [],
      uploading: false,
      fileSearch: '',

      // LINKS
      links: [],
      unsubLinks: null,
      createLinkDialog: false,
      savingLink: false,
      linkForm: { label: '', url: '', type: 'doc' },

      // ACTIVITY
      activity: [],
      loadingActivity: true,
      unsubActivity: null,

      // UI
      snack: { show: false, msg: '' }
    }
  },

  computed: {
    allAllowedNotes() {
      const arr = [...this.publicNotes, ...this.myPrivateNotes]
      return arr.sort((a, b) => this.toMs(b.createdAt) - this.toMs(a.createdAt))
    },
    visibleNotes() {
      return this.allAllowedNotes.filter(n => this.showPrivate ? true : !n.isPrivate)
    },
    filteredTasks() {
      let arr = [...this.tasks]
      if (this.taskFilter === 'mine') arr = arr.filter(t => t.authorUid === this.meUid || t.assigneeUid === this.meUid)
      if (this.taskFilter === 'open') arr = arr.filter(t => (t.status || 'open') !== 'done')
      if (this.taskFilter === 'done') arr = arr.filter(t => (t.status || 'open') === 'done')
      arr = arr.filter(t => !t.private || t.authorUid === this.meUid)
      if (this.taskSort === 'due-asc') arr.sort((a, b) => (this.toMs(a.dueAt) - this.toMs(b.dueAt)))
      if (this.taskSort === 'due-desc') arr.sort((a, b) => (this.toMs(b.dueAt) - this.toMs(a.dueAt)))
      if (this.taskSort === 'prio') arr.sort((a, b) => (this.prioWeight(b.priority) - this.prioWeight(a.priority)))
      return arr
    },
    openTaskCount() {
      return this.tasks.filter(t => (t.status || 'open') !== 'done' && (!t.private || t.authorUid === this.meUid)).length
    },
    filteredContacts() {
      const q = (this.contactSearch || '').toLowerCase()
      return this.contacts.filter(c => [c.name, c.email, c.title, c.role].join(' ').toLowerCase().includes(q))
    },
    filteredFiles() {
      const q = (this.fileSearch || '').toLowerCase()
      return this.files
        .filter(f => [f.name, f.mime].join(' ').toLowerCase().includes(q))
        .sort((a, b) => this.toMs(b.uploadedAt) - this.toMs(a.uploadedAt))
    }
  },

  async created() {
    const u = auth.currentUser
    if (!u) {
      this.$router.push('/')
      return
    }
    this.meUid = u.uid
    this.meName = u.displayName || u.email || 'User'

    const pSnap = await getDoc(doc(db, 'products', this.id))
    if (pSnap.exists()) this.product = { id: pSnap.id, ...pSnap.data() }

    // Presence
    this.touchPresence()
    this.presenceInterval = setInterval(this.touchPresence, 15000)
    const presQ = query(collection(db, 'products', this.id, 'presence'))
    onSnapshot(presQ, snap => {
      const now = Date.now()
      this.viewers = snap.docs
        .map(d => ({ uid: d.id, ...(d.data() || {}) }))
        .filter(v => now - (v.lastSeenMs || 0) < 30000)
    })

    // NOTES
    const pubQ = query(collection(db, 'products', this.id, 'notes'), where('isPrivate', '==', false))
    this.unsubPublic = onSnapshot(pubQ, snap => {
      this.publicNotes = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      this.loadingNotes = false
    }, () => { this.loadingNotes = false })

    const myPrivQ = query(
      collection(db, 'products', this.id, 'notes'),
      where('authorUid', '==', this.meUid),
      where('isPrivate', '==', true)
    )
    this.unsubPrivate = onSnapshot(myPrivQ, snap => {
      this.myPrivateNotes = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    })

    // TASKS
    const tQ = query(collection(db, 'products', this.id, 'tasks'))
    this.unsubTasks = onSnapshot(tQ, snap => {
      this.tasks = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      this.loadingTasks = false
    }, () => { this.loadingTasks = false })

    // CONTACTS
    const cQ = query(collection(db, 'products', this.id, 'contacts'))
    this.unsubContacts = onSnapshot(cQ, snap => {
      this.contacts = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      this.loadingContacts = false
    }, () => { this.loadingContacts = false })

    // FILES
    const fQ = query(collection(db, 'products', this.id, 'files'))
    this.unsubFiles = onSnapshot(fQ, snap => {
      this.files = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      this.loadingFiles = false
    }, () => { this.loadingFiles = false })

    // LINKS
    const lQ = query(collection(db, 'products', this.id, 'links'))
    this.unsubLinks = onSnapshot(lQ, snap => {
      this.links = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    })

    // ACTIVITY
    const aQ = query(collection(db, 'products', this.id, 'activity'))
    this.unsubActivity = onSnapshot(aQ, snap => {
      this.activity = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => this.toMs(b.createdAt) - this.toMs(a.createdAt))
      this.loadingActivity = false
    }, () => { this.loadingActivity = false })
  },

  beforeDestroy() {
    [this.unsubPublic, this.unsubPrivate, this.unsubTasks, this.unsubContacts, this.unsubFiles, this.unsubLinks, this.unsubActivity]
      .forEach(fn => {
        try {
          fn && fn()
        } catch (_) { }
      })
    clearInterval(this.presenceInterval)
  },

  methods: {
    // Presence
    async touchPresence() {
      try {
        await addDoc(collection(db, 'products', this.id, 'presence'), { _noop: true })
      } catch (_) { }
      const meRef = doc(db, 'products', this.id, 'presence', this.meUid)
      await updateDoc(meRef, {
        email: this.meName,
        lastSeenMs: Date.now()
      }).catch(async () => {
        await addDoc(collection(db, 'products', this.id, 'presence'), { _agent: 'init' }).catch(() => { })
        await updateDoc(meRef, { email: this.meName, lastSeenMs: Date.now() }).catch(() => { })
      })
    },

    // Visuals
    initials(name = '') {
      return (name || '??').split(/\s+/).map(x => x[0]).join('').slice(0, 2).toUpperCase()
    },
    avatarGradient(seed = '') {
      const gradients = [
        'linear-gradient(135deg, #00fff5, #7b2ff7)',
        'linear-gradient(135deg, #7b2ff7, #ff006e)',
        'linear-gradient(135deg, #ff006e, #ffbe0b)',
        'linear-gradient(135deg, #ffbe0b, #00fff5)'
      ]
      const index = Math.abs(String(seed).split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % gradients.length
      return gradients[index]
    },
    prioWeight(p) {
      return p === 'high' ? 2 : p === 'normal' ? 1 : 0
    },

    // Time helpers
    toDate(ts) {
      if (!ts) return null
      if (ts.toDate) return ts.toDate()
      if (ts.seconds) return new Date(ts.seconds * 1000)
      if (typeof ts === 'number') return new Date(ts)
      return null
    },
    toMs(ts) {
      const d = this.toDate(ts)
      return d ? d.getTime() : 0
    },
    prettyTs(ts) {
      const d = this.toDate(ts)
      if (!d) return ''
      return d.toLocaleString(undefined, { month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    },
    prettyDate(ts) {
      const d = this.toDate(ts)
      if (!d) return ''
      return d.toLocaleDateString()
    },
    isOverdue(t) {
      const due = this.toMs(t.dueAt)
      return due && due < Date.now() && (t.status || 'open') !== 'done'
    },

    // NOTES
    async addNote() {
      if (!this.noteText.trim()) return
      this.saving = true
      try {
        await addDoc(collection(db, 'products', this.id, 'notes'), {
          text: this.noteText.trim(),
          authorUid: this.meUid,
          authorName: this.meName,
          isPrivate: !!this.isPrivate,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        this.logActivity('note.add', `${this.meName} added a ${this.isPrivate ? 'private ' : ''}note`)
        this.noteText = ''
        this.isPrivate = false
        this.snack = { show: true, msg: '✓ Note added' }
      } catch (e) {
        this.snack = { show: true, msg: '✗ Failed to add note' }
      } finally {
        this.saving = false
      }
    },
    async delNote(n) {
      if (n.authorUid !== this.meUid) return
      this.delId = n.id
      try {
        await deleteDoc(doc(db, 'products', this.id, 'notes', n.id))
        this.logActivity('note.delete', `${this.meName} deleted a note`)
        this.snack = { show: true, msg: '✓ Note deleted' }
      } catch (e) {
        this.snack = { show: true, msg: '✗ Failed to delete note' }
      } finally {
        this.delId = ''
      }
    },

    // TASKS
    async addTask() {
      if (!this.taskForm.title.trim()) return
      this.savingTask = true
      try {
        const payload = {
          title: this.taskForm.title.trim(),
          priority: this.taskForm.priority || 'normal',
          status: 'open',
          private: !!this.taskForm.private,
          authorUid: this.meUid,
          assigneeUid: this.meUid,
          dueAt: this.taskForm.dueDate ? new Date(this.taskForm.dueDate).getTime() : null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }
        await addDoc(collection(db, 'products', this.id, 'tasks'), payload)
        this.logActivity('task.add', `${this.meName} added task "${payload.title}"`)
        this.taskForm = { title: '', priority: 'normal', dueDate: null, private: false }
        this.snack = { show: true, msg: '✓ Task added' }
      } catch (e) {
        this.snack = { show: true, msg: '✗ Failed to add task' }
      } finally {
        this.savingTask = false
      }
    },
    async toggleTask(t) {
      try {
        const next = (t.status || 'open') === 'done' ? 'open' : 'done'
        await updateDoc(doc(db, 'products', this.id, 'tasks', t.id), {
          status: next,
          updatedAt: serverTimestamp()
        })
        this.logActivity('task.toggle', `${this.meName} marked task "${t.title}" ${next === 'done' ? 'done' : 'open'}`)
      } catch (e) {
        this.snack = { show: true, msg: '✗ Failed to update task' }
      }
    },
    async deleteTask(t) {
      try {
        await deleteDoc(doc(db, 'products', this.id, 'tasks', t.id))
        this.logActivity('task.delete', `${this.meName} deleted task "${t.title}"`)
        this.snack = { show: true, msg: '✓ Task deleted' }
      } catch (e) {
        this.snack = { show: true, msg: '✗ Failed to delete task' }
      }
    },

    // CONTACTS
    async addContact() {
      this.savingContact = true
      try {
        await addDoc(collection(db, 'products', this.id, 'contacts'), {
          ...this.contactForm,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        this.logActivity('contact.add', `${this.meName} added contact ${this.contactForm.name}`)
        this.contactForm = { name: '', title: '', email: '', phone: '', role: '', notes: '' }
        this.contactDialog = false
        this.snack = { show: true, msg: '✓ Contact added' }
      } catch (e) {
        this.snack = { show: true, msg: '✗ Failed to add contact' }
      } finally {
        this.savingContact = false
      }
    },
    async removeContact(c) {
      try {
        await deleteDoc(doc(db, 'products', this.id, 'contacts', c.id))
        this.logActivity('contact.delete', `${this.meName} removed contact ${c.name}`)
        this.snack = { show: true, msg: '✓ Contact removed' }
      } catch (e) {
        this.snack = { show: true, msg: '✗ Failed to remove contact' }
      }
    },

    // FILES
    handleFileSelect(e) {
      const files = Array.from(e.target.files)
      this.fileToUpload = [...this.fileToUpload, ...files]
    },
    handleDrop(e) {
      const files = Array.from(e.dataTransfer.files)
      this.fileToUpload = [...this.fileToUpload, ...files]
    },
    removeFileFromUpload(idx) {
      this.fileToUpload.splice(idx, 1)
    },
    getPreviewUrl(file) {
      return URL.createObjectURL(file)
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },
    async uploadFiles() {
  if (this.fileToUpload.length === 0) return
  this.uploading = true
  
  console.log('🚀 Starting upload for', this.fileToUpload.length, 'files')
  console.log('📁 Product ID:', this.id)
  console.log('👤 User:', this.meUid, this.meName)
  
  try {
    for (const file of this.fileToUpload) {
      console.log('📤 Uploading:', file.name)
      
      // Upload to Storage
      const path = `products/${this.id}/${Date.now()}_${file.name}`
      const storageRef = sRef(storage, path)
      await uploadBytes(storageRef, file)
      console.log('✅ Storage upload complete')
      
      // Get download URL
      const url = await getDownloadURL(storageRef)
      console.log('🔗 Download URL obtained')
      
      // Create Firestore document
      const firestoreData = {
        name: file.name,
        path,
        url,
        size: file.size,
        mime: file.type,
        uploadedAt: serverTimestamp(),
        uploadedBy: this.meUid,
        uploadedByName: this.meName
      }
      
      console.log('💾 Creating Firestore document...')
      const docRef = await addDoc(collection(db, 'products', this.id, 'files'), firestoreData)
      console.log('✅ Firestore document created:', docRef.id)
      
      // Log activity
      await this.logActivity('file.upload', `${this.meName} uploaded ${file.name}`)
      console.log('✅ Activity logged')
    }
    
    this.fileToUpload = []
    this.snack = { show: true, msg: '✓ Files uploaded successfully' }
    console.log('🎉 All uploads complete!')
  } catch (e) {
    console.error('❌ Upload error:', e)
    console.error('Error code:', e.code)
    console.error('Error message:', e.message)
    this.snack = { show: true, msg: '✗ Upload failed: ' + e.message }
  } finally {
    this.uploading = false
  }
},
    async deleteFile(f) {
      try {
        await deleteDoc(doc(db, 'products', this.id, 'files', f.id))
        if (f.path) {
          try {
            await deleteObject(sRef(storage, f.path))
          } catch (_) { }
        }
        this.logActivity('file.delete', `${this.meName} deleted ${f.name}`)
        this.snack = { show: true, msg: '✓ File deleted' }
      } catch (e) {
        this.snack = { show: true, msg: '✗ Failed to delete file' }
      }
    },
    getFileIconPath(mime) {
      if (mime.includes('pdf')) return 'M13.3333 2H4C2.93913 2 2.08696 2.85217 2.08696 3.91304V20.087C2.08696 21.1478 2.93913 22 4 22H16C17.0609 22 17.913 21.1478 17.913 20.087V7.47826L13.3333 2Z'
      if (mime.includes('sheet') || mime.includes('excel')) return 'M20 4H4C2.89543 4 2 4.89543 2 6V18C2 19.1046 2.89543 20 4 20H20C21.1046 20 22 19.1046 22 18V6C22 4.89543 21.1046 4 20 4Z'
      if (mime.includes('presentation') || mime.includes('powerpoint')) return 'M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z'
      if (mime.includes('zip')) return 'M18 10V8C18 6.89543 17.1046 6 16 6H12V2H8V6H4C2.89543 6 2 6.89543 2 8V18C2 19.1046 2.89543 20 4 20H16C17.1046 20 18 19.1046 18 18V16'
      return 'M13 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V9L13 2Z'
    },

    // LINKS
    async addLink() {
      this.savingLink = true
      try {
        await addDoc(collection(db, 'products', this.id, 'links'), {
          ...this.linkForm,
          createdAt: serverTimestamp()
        })
        this.logActivity('link.add', `${this.meName} added link ${this.linkForm.label}`)
        this.linkForm = { label: '', url: '', type: 'doc' }
        this.createLinkDialog = false
        this.snack = { show: true, msg: '✓ Link added' }
      } catch (e) {
        this.snack = { show: true, msg: '✗ Failed to add link' }
      } finally {
        this.savingLink = false
      }
    },

    // ACTIVITY
    getActivityType(type) {
      if (type && type.startsWith('note')) return 'note'
      if (type && type.startsWith('task')) return 'task'
      if (type && type.startsWith('contact')) return 'contact'
      if (type && type.startsWith('file')) return 'file'
      if (type && type.startsWith('link')) return 'link'
      return 'other'
    },
    getActivityIcon(type) {
      if (type && type.startsWith('note')) return '<path d="M13.5 3H4.5C3.67157 3 3 3.67157 3 4.5V13.5C3 14.3284 3.67157 15 4.5 15H13.5C14.3284 15 15 14.3284 15 13.5V4.5C15 3.67157 14.3284 3 13.5 3Z" stroke="currentColor" stroke-width="1.5"/>'
      if (type && type.startsWith('task')) return '<path d="M13.5 3H4.5C3.67157 3 3 3.67157 3 4.5V13.5C3 14.3284 3.67157 15 4.5 15H13.5C14.3284 15 15 14.3284 15 13.5V4.5C15 3.67157 14.3284 3 13.5 3Z" stroke="currentColor" stroke-width="1.5"/><path d="M6 9L8 11L12 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>'
      if (type && type.startsWith('contact')) return '<path d="M9 13.5C11.4853 13.5 13.5 11.4853 13.5 9C13.5 6.51472 11.4853 4.5 9 4.5C6.51472 4.5 4.5 6.51472 4.5 9C4.5 11.4853 6.51472 13.5 9 13.5Z" stroke="currentColor" stroke-width="1.5"/>'
      if (type && type.startsWith('file')) return '<path d="M10.5 3H4.5C3.67157 3 3 3.67157 3 4.5V13.5C3 14.3284 3.67157 15 4.5 15H13.5C14.3284 15 15 14.3284 15 13.5V7.5L10.5 3Z" stroke="currentColor" stroke-width="1.5"/>'
      if (type && type.startsWith('link')) return '<path d="M8.33333 10.8333C8.69103 11.3118 9.14777 11.7074 9.67235 11.9943L14.6458 11.3142" stroke="currentColor" stroke-width="1.5"/>'
      return '<circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/>'
    },
    async logActivity(type, message) {
      try {
        await addDoc(collection(db, 'products', this.id, 'activity'), {
          type,
          message,
          actorUid: this.meUid,
          actorName: this.meName,
          createdAt: serverTimestamp()
        })
      } catch (_) { }
    }
  }
}
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@300;400;500;600;700;800&display=swap');
  
  :root {
    --color-bg: #0a0118;
    --color-surface: #1a0b2e;
    --color-text: #ffffff;
    --color-text-muted: #b8b8d1;
    --color-accent: #00fff5;
    --color-accent-2: #7b2ff7;
    --color-pink: #ff006e;
    --color-yellow: #ffbe0b;
    --font-display: 'Sora', sans-serif;
    --font-mono: 'Space Mono', monospace;
  }
  
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  /* Page Background */
  .workspace-page {
    min-height: 100vh;
    background: 
      radial-gradient(circle at 15% 20%, rgba(123, 47, 247, 0.3), transparent 40%),
      radial-gradient(circle at 85% 80%, rgba(255, 0, 110, 0.3), transparent 40%),
      radial-gradient(circle at 50% 50%, rgba(0, 255, 245, 0.2), transparent 50%),
      linear-gradient(135deg, #1a0b2e 0%, #0a0118 50%, #1a0b2e 100%);
    background-size: 200% 200%;
    animation: bg-breathe 25s ease-in-out infinite;
    position: relative;
    font-family: var(--font-display);
    padding: 40px 0 80px;
  }
  
  @keyframes bg-breathe {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  
  .page-grid {
    position: fixed;
    inset: 0;
    background-image: 
      linear-gradient(to right, rgba(0, 255, 245, 0.08) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(123, 47, 247, 0.08) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
    animation: grid-flow 35s linear infinite;
    pointer-events: none;
  }
  
  @keyframes grid-flow {
    0% { transform: translate(0, 0); }
    100% { transform: translate(60px, 60px); }
  }
  
  .page-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(140px);
    opacity: 0.5;
    animation: orb-float 18s ease-in-out infinite;
    mix-blend-mode: screen;
    pointer-events: none;
  }
  
  .orb-1 {
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, var(--color-accent), transparent);
    top: -200px;
    left: -200px;
  }
  
  .orb-2 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, var(--color-pink), transparent);
    bottom: -150px;
    right: -150px;
    animation-delay: 6s;
  }
  
  @keyframes orb-float {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(50px, -50px) scale(1.1); }
    66% { transform: translate(-40px, 40px) scale(0.9); }
  }
  
  /* Container */
  .workspace-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 24px;
    position: relative;
    z-index: 2;
  }
  
  .workspace-grid {
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    gap: 24px;
    align-items: start;
  }
  
  /* Overview Card */
  .overview-card {
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid var(--color-accent);
    border-radius: 20px;
    backdrop-filter: blur(20px);
    box-shadow: 0 0 40px rgba(0, 255, 245, 0.3);
    margin-bottom: 24px;
    overflow: hidden;
  }
  
  .overview-header {
    background: linear-gradient(135deg, var(--color-accent), var(--color-accent-2));
    padding: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  
  .header-main {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
  }
  
  .product-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    flex-shrink: 0;
  }
  
  .product-name {
    font-size: 24px;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 4px;
  }
  
  .product-meta {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  .presence-avatars {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .presence-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: #ffffff;
    font-family: var(--font-mono);
    border: 2px solid rgba(255, 255, 255, 0.3);
    flex-shrink: 0;
  }
  
  .presence-count {
    padding: 4px 10px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    color: #ffffff;
    font-family: var(--font-mono);
  }
  
  .product-status {
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    text-transform: capitalize;
    font-family: var(--font-mono);
    flex-shrink: 0;
  }
  
  .status-active {
    background: rgba(34, 197, 94, 0.2);
    color: #4ade80;
    border: 1px solid rgba(34, 197, 94, 0.4);
  }
  
  .status-paused {
    background: rgba(255, 190, 11, 0.2);
    color: var(--color-yellow);
    border: 1px solid rgba(255, 190, 11, 0.4);
  }
  
  .status-archived {
    background: rgba(156, 163, 175, 0.2);
    color: #9ca3af;
    border: 1px solid rgba(156, 163, 175, 0.4);
  }
  
  .overview-body {
    padding: 24px;
  }
  
  .section-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(255, 255, 255, 0.6);
    font-family: var(--font-mono);
    margin-bottom: 8px;
  }
  
  .product-summary {
    color: var(--color-text-muted);
    line-height: 1.6;
    margin-bottom: 24px;
  }
  
  /* KPI Mini Cards */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  
  .kpi-mini {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 12px;
    transition: all 0.3s ease;
  }
  
  .kpi-mini svg {
    flex-shrink: 0;
  }
  
  .kpi-tasks {
    border: 2px solid rgba(0, 255, 245, 0.3);
    color: var(--color-accent);
  }
  
  .kpi-tasks:hover {
    border-color: var(--color-accent);
    box-shadow: 0 0 20px rgba(0, 255, 245, 0.4);
  }
  
  .kpi-contacts {
    border: 2px solid rgba(255, 190, 11, 0.3);
    color: var(--color-yellow);
  }
  
  .kpi-contacts:hover {
    border-color: var(--color-yellow);
    box-shadow: 0 0 20px rgba(255, 190, 11, 0.4);
  }
  
  .kpi-files {
    border: 2px solid rgba(255, 0, 110, 0.3);
    color: var(--color-pink);
  }
  
  .kpi-files:hover {
    border-color: var(--color-pink);
    box-shadow: 0 0 20px rgba(255, 0, 110, 0.4);
  }
  
  .kpi-value {
    font-size: 24px;
    font-weight: 900;
    color: #ffffff;
    font-family: var(--font-mono);
    line-height: 1;
  }
  
  .kpi-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(255, 255, 255, 0.6);
    font-family: var(--font-mono);
  }
  
  /* Links Card */
  .links-card {
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid var(--color-pink);
    border-radius: 20px;
    backdrop-filter: blur(20px);
    box-shadow: 0 0 40px rgba(255, 0, 110, 0.3);
    overflow: hidden;
  }
  
  .card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: #ffffff;
    font-size: 16px;
    font-weight: 700;
  }
  
  .card-header svg {
    color: var(--color-pink);
    filter: drop-shadow(0 0 8px var(--color-pink));
  }
  
  .card-header span {
    flex: 1;
  }
  
  .btn-add-link {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .btn-add-link:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
  }
  
  .links-body {
    padding: 20px 24px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .link-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: linear-gradient(135deg, rgba(123, 47, 247, 0.2), rgba(255, 0, 110, 0.2));
    border: 1px solid rgba(123, 47, 247, 0.4);
    border-radius: 8px;
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.2s ease;
  }
  
  .link-chip:hover {
    border-color: var(--color-accent);
    box-shadow: 0 0 15px rgba(0, 255, 245, 0.4);
    transform: translateY(-2px);
  }
  
  .empty-links {
    color: var(--color-text-muted);
    font-size: 14px;
    font-style: italic;
  }
  
  /* Workspace Card */
  .workspace-card {
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid var(--color-accent-2);
    border-radius: 20px;
    backdrop-filter: blur(20px);
    box-shadow: 0 0 40px rgba(123, 47, 247, 0.3);
    overflow: hidden;
    position: sticky;
    top: 24px;
  }
  
  /* Tabs */
  .tabs-header {
    display: flex;
    background: linear-gradient(90deg, var(--color-accent-2), var(--color-pink));
    padding: 8px;
    gap: 4px;
    overflow-x: auto;
  }
  
  .tab-button {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 16px;
    background: transparent;
    border: none;
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
  }
  
  .tab-button:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
  
  .tab-button.active {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
  
  .tab-content {
    max-height: calc(100vh - 200px);
    overflow-y: auto;
  }
  
  .tab-panel {
    padding: 24px;
    animation: fade-in 0.3s ease;
  }
  
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  /* Form Elements */
  input[type="text"],
  input[type="email"],
  input[type="tel"],
  input[type="url"],
  input[type="date"],
  textarea,
  select {
    width: 100%;
    padding: 12px 14px;
    background: rgba(255, 255, 255, 0.08);
    border: 2px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    color: #ffffff !important;
    font-family: var(--font-display);
    font-size: 14px;
    outline: none;
    transition: all 0.3s ease;
  }
  
  input::placeholder,
  textarea::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  input:focus,
  textarea:focus,
  select:focus {
    background: rgba(255, 255, 255, 0.12);
    border-color: var(--color-accent);
    box-shadow: 0 0 20px rgba(0, 255, 245, 0.3);
  }
  
  select {
    cursor: pointer;
  }
  
  select option {
    background: var(--color-surface);
    color: #ffffff;
  }
  
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #ffffff;
    font-size: 14px;
    cursor: pointer;
    user-select: none;
  }
  
  .checkbox-label input[type="checkbox"] {
    width: auto;
    cursor: pointer;
  }
  
  /* Buttons */
  .btn-primary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 20px;
    background: linear-gradient(135deg, var(--color-accent), var(--color-accent-2));
    border: none;
    border-radius: 8px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0, 255, 245, 0.3);
    white-space: nowrap;
  }
  
  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(0, 255, 245, 0.5);
  }
  
  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .btn-secondary {
    padding: 10px 20px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .btn-secondary:hover {
    background: rgba(255, 255, 255, 0.15);
  }
  
  .btn-delete {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 6px;
    color: #f87171;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  
  .btn-delete:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.2);
    border-color: #f87171;
    transform: scale(1.1);
  }
  
  .btn-delete:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Notes */
  .notes-form {
    margin-bottom: 24px;
  }
  
  .notes-form textarea {
    margin-bottom: 12px;
    resize: vertical;
    min-height: 80px;
  }
  
  .form-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  
  .section-divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
    margin: 24px 0;
  }
  
  .notes-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }
  
  .section-title {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--color-accent);
    font-family: var(--font-mono);
  }
  
  .note-count {
    padding: 3px 8px;
    background: rgba(0, 255, 245, 0.15);
    border: 1px solid rgba(0, 255, 245, 0.3);
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    color: var(--color-accent);
    font-family: var(--font-mono);
  }
  
  .notes-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .note-item {
    display: grid;
    grid-template-columns: 40px 1fr auto;
    gap: 12px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    transition: all 0.2s ease;
  }
  
  .note-item:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(0, 255, 245, 0.3);
  }
  
  .note-avatar {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    font-family: var(--font-mono);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  .note-content {
    flex: 1;
    min-width: 0;
  }
  
  .note-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }
  
  .note-author {
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
  }
  
  .note-badge {
    padding: 2px 8px;
    background: rgba(156, 163, 175, 0.2);
    border: 1px solid rgba(156, 163, 175, 0.4);
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    color: #9ca3af;
    font-family: var(--font-mono);
  }
  
  .note-time {
    font-size: 12px;
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }
  
  .note-text {
    color: var(--color-text-muted);
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
  }
  
  /* Tasks */
  .task-form {
    margin-bottom: 20px;
  }
  
  .task-input {
    margin-bottom: 12px;
  }
  
  .task-form-row {
    display: grid;
    grid-template-columns: 120px 160px auto 1fr;
    gap: 12px;
    align-items: center;
  }
  
  .task-select,
  .task-date {
    padding: 10px 12px;
  }
  
  .task-filters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }
  
  .filter-chips {
    display: flex;
    gap: 6px;
  }
  
  .filter-chip {
    padding: 6px 14px;
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid rgba(255, 255, 255, 0.15);
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    font-weight: 600;
    text-transform: capitalize;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .filter-chip:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.25);
  }
  
  .filter-chip.active {
    background: linear-gradient(135deg, var(--color-accent), var(--color-accent-2));
    border-color: var(--color-accent);
    color: #ffffff;
    box-shadow: 0 4px 15px rgba(0, 255, 245, 0.4);
  }
  
  .sort-select {
    min-width: 180px;
  }
  
  .tasks-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .task-item {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 12px;
    align-items: center;
    padding: 14px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    transition: all 0.2s ease;
  }
  
  .task-item:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(123, 47, 247, 0.3);
  }
  
  .task-checkbox {
    position: relative;
    display: flex;
    cursor: pointer;
  }
  
  .task-checkbox input {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }
  
  .task-title {
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 4px;
  }
  
  .task-title.completed {
    text-decoration: line-through;
    color: var(--color-text-muted);
  }
  
  .task-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  
  .priority-badge {
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    text-transform: capitalize;
    font-family: var(--font-mono);
  }
  
  .priority-high {
    background: rgba(239, 68, 68, 0.2);
    color: #f87171;
    border: 1px solid rgba(239, 68, 68, 0.4);
  }
  
  .priority-normal {
    background: rgba(59, 130, 246, 0.2);
    color: #60a5fa;
    border: 1px solid rgba(59, 130, 246, 0.4);
  }
  
  .priority-low {
    background: rgba(156, 163, 175, 0.2);
    color: #9ca3af;
    border: 1px solid rgba(156, 163, 175, 0.4);
  }
  
  .due-date {
    font-size: 12px;
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }
  
  .due-date.overdue {
    color: #f87171;
    font-weight: 700;
  }
  
  .no-due {
    font-size: 12px;
    color: var(--color-text-muted);
    font-style: italic;
  }
  
  /* Contacts */
  .contacts-header {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
  }
  
  .search-input {
    flex: 1;
  }
  
  .contacts-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .contact-item {
    display: grid;
    grid-template-columns: 40px 1fr auto auto;
    gap: 12px;
    align-items: center;
    padding: 14px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    transition: all 0.2s ease;
  }
  
  .contact-item:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 190, 11, 0.3);
  }
  
  .contact-avatar {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    font-family: var(--font-mono);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  .contact-name {
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 4px;
  }
  
  .contact-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--color-text-muted);
    flex-wrap: wrap;
  }
  
  .contact-meta a {
    color: var(--color-accent);
    text-decoration: none;
    transition: all 0.2s ease;
  }
  
  .contact-meta a:hover {
    text-decoration: underline;
  }
  
  .contact-role {
    padding: 3px 8px;
    background: rgba(123, 47, 247, 0.2);
    border: 1px solid rgba(123, 47, 247, 0.4);
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    color: var(--color-accent-2);
    font-family: var(--font-mono);
    text-transform: capitalize;
  }
  
  /* Files */
  .files-upload {
    margin-bottom: 24px;
  }
  
  .upload-area {
    padding: 40px 24px;
    background: rgba(255, 255, 255, 0.03);
    border: 2px dashed rgba(0, 255, 245, 0.3);
    border-radius: 12px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .upload-area:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: var(--color-accent);
    box-shadow: 0 0 30px rgba(0, 255, 245, 0.2);
  }
  
  .upload-area svg {
    color: var(--color-accent);
    margin-bottom: 16px;
  }
  
  .upload-text {
    font-size: 16px;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 8px;
  }
  
  .upload-hint {
    font-size: 13px;
    color: var(--color-text-muted);
  }
  
  .files-preview {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
    margin-top: 16px;
  }
  
  .file-preview {
    position: relative;
    padding: 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  
  .file-preview img {
    width: 100%;
    height: 120px;
    object-fit: cover;
    border-radius: 6px;
  }
  
  .file-name {
    font-size: 12px;
    font-weight: 600;
    color: #ffffff;
    text-align: center;
    word-break: break-all;
  }
  
  .file-size {
    font-size: 11px;
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }
  
  .btn-remove {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(239, 68, 68, 0.9);
    border: none;
    border-radius: 50%;
    color: #ffffff;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .btn-remove:hover {
    background: #f87171;
    transform: scale(1.1);
  }
  
  .btn-upload {
    width: 100%;
    padding: 12px;
    background: linear-gradient(135deg, var(--color-pink), var(--color-yellow));
    border: none;
    border-radius: 8px;
    color: #ffffff;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(255, 0, 110, 0.4);
    margin-top: 16px;
  }
  
  .btn-upload:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 30px rgba(255, 0, 110, 0.6);
  }
  
  .btn-upload:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .files-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
  }
  
  .search-input-small {
    max-width: 240px;
  }
  
  .files-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .file-item {
    display: grid;
    grid-template-columns: 48px 1fr auto;
    gap: 12px;
    align-items: center;
    padding: 12px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    transition: all 0.2s ease;
  }
  
  .file-item:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 0, 110, 0.3);
  }
  
  .file-preview-thumb {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.05);
  }
  
  .file-preview-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .file-preview-thumb svg {
    color: var(--color-pink);
  }
  
  .file-content a {
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    text-decoration: none;
    transition: all 0.2s ease;
    display: block;
    margin-bottom: 4px;
  }
  
  .file-content a:hover {
    color: var(--color-accent);
  }
  
  .file-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }
  
  /* Activity */
  .activity-timeline {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  
  .activity-item {
    display: grid;
    grid-template-columns: 32px 1fr;
    gap: 12px;
  }
  
  .activity-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  
  .icon-note {
    background: rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.4);
    color: #818cf8;
  }
  
  .icon-task {
    background: rgba(168, 85, 247, 0.2);
    border: 1px solid rgba(168, 85, 247, 0.4);
    color: #c084fc;
  }
  
  .icon-contact {
    background: rgba(34, 197, 94, 0.2);
    border: 1px solid rgba(34, 197, 94, 0.4);
    color: #4ade80;
  }
  
  .icon-file {
    background: rgba(59, 130, 246, 0.2);
    border: 1px solid rgba(59, 130, 246, 0.4);
    color: #60a5fa;
  }
  
  .icon-link {
    background: rgba(20, 184, 166, 0.2);
    border: 1px solid rgba(20, 184, 166, 0.4);
    color: #5eead4;
  }
  
  .icon-other {
    background: rgba(156, 163, 175, 0.2);
    border: 1px solid rgba(156, 163, 175, 0.4);
    color: #9ca3af;
  }
  
  .activity-actor {
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 2px;
  }
  
  .activity-time {
    font-size: 11px;
    color: var(--color-text-muted);
    font-family: var(--font-mono);
    margin-bottom: 6px;
  }
  
  .activity-message {
    font-size: 13px;
    color: var(--color-text-muted);
    line-height: 1.5;
  }
  
  /* Loading/Empty States */
  .loading-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 24px;
    text-align: center;
  }
  
  .loader {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.1);
    border-top-color: var(--color-accent);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .loading-state p,
  .empty-state p {
    color: var(--color-text-muted);
    font-size: 15px;
  }
  
  .empty-state svg {
    color: var(--color-text-muted);
    opacity: 0.3;
    margin-bottom: 16px;
  }
  
  /* Modals */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 24px;
    animation: fade-in 0.2s ease;
  }
  
  .modal-dialog {
    width: 100%;
    max-width: 600px;
    background: linear-gradient(135deg, rgba(26, 11, 46, 0.98), rgba(10, 1, 24, 0.98));
    border: 2px solid var(--color-accent);
    border-radius: 20px;
    box-shadow: 0 0 60px rgba(0, 255, 245, 0.4);
    animation: modal-in 0.3s ease;
  }
  
  .modal-dialog.modal-sm {
    max-width: 480px;
  }
  
  @keyframes modal-in {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
  
  .modal-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: #ffffff;
    font-size: 18px;
    font-weight: 700;
  }
  
  .modal-header svg {
    color: var(--color-accent);
    filter: drop-shadow(0 0 8px var(--color-accent));
  }
  
  .modal-header span {
    flex: 1;
  }
  
  .btn-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 6px;
    color: #ffffff;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .btn-close:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: rotate(90deg);
  }
  
  .modal-body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  
  .modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  /* Toast */
  .toast-notification {
    position: fixed;
    bottom: 24px;
    right: 24px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background: linear-gradient(135deg, rgba(123, 47, 247, 0.95), rgba(255, 0, 110, 0.95));
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
    z-index: 1001;
    animation: toast-in 0.3s ease;
  }
  
  @keyframes toast-in {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .toast-notification button {
    background: transparent;
    border: none;
    color: #ffffff;
    font-size: 18px;
    cursor: pointer;
    padding: 0 4px;
    transition: transform 0.2s ease;
  }
  
  .toast-notification button:hover {
    transform: scale(1.2);
  }
  
  /* Responsive */
  @media (max-width: 1200px) {
    .workspace-grid {
      grid-template-columns: 1fr;
    }
  
    .workspace-card {
      position: relative;
      top: 0;
    }
  }
  
  @media (max-width: 768px) {
    .workspace-page {
      padding: 20px 0 40px;
    }
  
    .workspace-container {
      padding: 0 16px;
    }
  
    .kpi-grid {
      grid-template-columns: 1fr;
    }
  
    .tabs-header {
      overflow-x: auto;
    }
  
    .task-form-row {
      grid-template-columns: 1fr;
    }
  
    .contacts-header {
      flex-direction: column;
    }
  
    .search-input,
    .search-input-small {
      max-width: none;
    }
  
    .files-preview {
      grid-template-columns: 1fr;
    }
  
    .modal-dialog {
      max-width: 100%;
    }
  }
  
  @media (max-width: 600px) {
    .tab-button span {
      display: none;
    }
  
    .tab-button {
      padding: 10px 12px;
    }
  }
  </style>